/**
 * @file JS - Adapter - VectorLayerAdapter.js
 * @author Adam Lemire
 * @since 2020/07/22
 * @version 1.0
 * @summary Dernières modifications : 2021/12/15
 */

import { LayerProperties } from '../helpers/Selectors.js';
import { FeatureType, StyleTypes } from '../helpers/Constants.js';
import PolygonVectorLayerController from '../controllers/Layer/VectorLayer/PolygonVectorLayerController.js';
import LineVectorLayerController from '../controllers/Layer/VectorLayer/LineVectorLayerController.js';
import PointVectorLayerController from '../controllers/Layer/VectorLayer/PointVectorLayerController.js';
import VectorLayer from '../classes/Layer/VectorLayer.js'; // eslint-disable-line no-unused-vars
import VectorLayerStyle from '../classes/Layer/VectorLayerStyle.js'; // eslint-disable-line no-unused-vars

/** adaptateur des différents VectorLayerControllers */
export default class VectorLayerAdapter {
  constructor (featureType) {
    /** @type { FeatureType } */
    this._type = featureType;
  }

  get type () { return this._type; }

  set type (featureType) {
    this._type = featureType;
  }

  /**
   * @param { VectorLayer } layer
   * @returns { Map < number, GroupStyle > }
   */
  getNewSymbolStyles (layer) {
    switch (this._type) {
      case FeatureType.POLYGON:
        return PolygonVectorLayerController.getNewSymbolStyles(layer);
      case FeatureType.LINE:
        return LineVectorLayerController.getNewSymbolStyles(layer);
      case FeatureType.POINT:
        return PointVectorLayerController.getNewSymbolStyles(layer);
      default:
        console.log('feature type pas pret');
    }
  }

  obtenirNouveauxStylesSymbolesGradués (classification) {
    switch (this._type) {
      case FeatureType.POLYGON:
        return PolygonVectorLayerController.getNewGraduatedSymbolStyles(classification);
      case FeatureType.LINE:
        return LineVectorLayerController.getNewGraduatedSymbolStyles(classification);
      case FeatureType.POINT:
        return PointVectorLayerController.getNewGraduatedSymbolStyles(classification);
      default:
        console.log('feature type pas pret');
    }
  }

  /** @param { FeatureType } featureType */
  createSymbolPreview (featureType) {
    const mFeatureType = featureType || this._type;
    const parentDiv = $(LayerProperties.LAYER_SYMBOL_PREVIEW);
    const apercuAutres = $(LayerProperties.SYMBOLE_AUTRES_APERCU);
    parentDiv.empty();
    apercuAutres.empty();

    switch (mFeatureType) {
      case FeatureType.POLYGON:
        parentDiv.append(PolygonVectorLayerController.createSymbolPreviewDiv());
        apercuAutres.append(PolygonVectorLayerController.creerDivApercuSymboleAutres());
        break;
      case FeatureType.LINE:
        parentDiv.append(LineVectorLayerController.createSymbolPreviewDiv());
        apercuAutres.append(LineVectorLayerController.creerDivApercuSymboleAutres());
        break;
      case FeatureType.POINT:
        parentDiv.append(PointVectorLayerController.createSymbolPreviewDiv());
        apercuAutres.append(PointVectorLayerController.creerDivApercuSymboleAutres());
        break;
      default:
        console.log('erreur : Type inconnu' + this);
    }
  }

  /** @param { FeatureType } featureType */
  showFeatureStyleProperties (featureType) {
    const mFeatureType = featureType || this._type;
    switch (mFeatureType) {
      case FeatureType.POLYGON:
        PolygonVectorLayerController.showFeatureStyleProperties();
        break;
      case FeatureType.LINE:
        LineVectorLayerController.showFeatureStyleProperties();
        break;
      case FeatureType.POINT:
        PointVectorLayerController.showFeatureStyleProperties();
        break;
      default:
        console.log('erreur : Type inconnu' + this);
    }
  }

  createGroupsPropertiesRows (symbolStyles) {
    console.log(symbolStyles);
    switch (this._type) {
      case FeatureType.POLYGON:
        PolygonVectorLayerController.createGroupsPropertiesRows(symbolStyles);
        break;
      case FeatureType.LINE:
        LineVectorLayerController.createGroupsPropertiesRows(symbolStyles);
        break;
      case FeatureType.POINT:
        PointVectorLayerController.createGroupsPropertiesRows(symbolStyles);
        break;
      default:
        console.log('feature type pas pret');
    }
  }

  /**
   * @param { GroupStyle } advancedStyleObject
   */
  updatePropertyEditorValues (advancedStyleObject) {
    switch (this._type) {
      case FeatureType.POLYGON:
        PolygonVectorLayerController.updatePropertyEditorValues(advancedStyleObject);
        break;
      case FeatureType.LINE:
        LineVectorLayerController.updatePropertyEditorValues(advancedStyleObject);
        break;
      case FeatureType.POINT:
        PointVectorLayerController.updatePropertyEditorValues(advancedStyleObject);
        break;
      default:
        console.log('erreur : Type inconnu' + this);
    }
    this.createSymbolPreview();
  }

  /**
   * @param { VectorLayerStyle } vectorLayerStyle
   */
  updatePropertyEditorGroupSymbol (vectorLayerStyle) {
    switch (this._type) {
      case FeatureType.POLYGON:
        PolygonVectorLayerController.updatePropertyEditorGroupSymbol(vectorLayerStyle);
        break;
      case FeatureType.LINE:
        LineVectorLayerController.updatePropertyEditorGroupSymbol(vectorLayerStyle);
        break;
      case FeatureType.POINT:
        PointVectorLayerController.updatePropertyEditorGroupSymbol(vectorLayerStyle);
        break;
      default:
        console.log('erreur : Type inconnu' + this);
    }
  }

  /**
   * @static
   * @param { VectorLayerStyle } vectorLayerStyle
   * @param { string } featureType
   * @returns { string }
   */
  static getLegendSymbolDiv (vectorLayerStyle, featureType) {
    if (!vectorLayerStyle || vectorLayerStyle.styleType === StyleTypes.SINGLE) {
      switch (featureType) {
        case FeatureType.POLYGON:
          return PolygonVectorLayerController.createSingleLegendSymbolDiv(vectorLayerStyle.singleStyle);
        case FeatureType.LINE:
          return LineVectorLayerController.createSingleLegendSymbolDiv(vectorLayerStyle.singleStyle);
        case FeatureType.POINT:
          return PointVectorLayerController.createSingleLegendSymbolDiv(vectorLayerStyle.singleStyle);
        default:
          console.log('feature type pas pret');
      }
    } else {
      switch (featureType) {
        case FeatureType.POLYGON:
          return PolygonVectorLayerController.createMultiLegendSymbolsDiv(vectorLayerStyle.symbolStyles);
        case FeatureType.LINE:
          return LineVectorLayerController.createMultiLegendSymbolsDiv(vectorLayerStyle.symbolStyles);
        case FeatureType.POINT:
          return PointVectorLayerController.createMultiLegendSymbolsDiv(vectorLayerStyle.symbolStyles);
        default:
          console.log('feature type pas pret');
      }
    }
  }
}
